import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Send, Trash2 } from "lucide-react";

const STATUS_OPTS = ["aberto", "em_andamento", "aguardando_cliente", "resolvido", "fechado"];
const PRIORITY_OPTS = ["baixa", "media", "alta", "urgente"];

export default function TicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/tickets/${id}`);
      setTicket(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      nav("/tickets");
    }
  }, [id, nav]);

  useEffect(() => {
    load();
    if (user.role !== "cliente") {
      api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "tecnico" || u.role === "admin"))).catch(() => {});
    }

  }, [load, user.role]);

  if (!ticket) return null;

  const canManage = user.role === "admin" || user.role === "tecnico";

  const update = async (patch) => {
    try {
      const r = await api.patch(`/tickets/${id}`, patch);
      setTicket(r.data);
      toast.success("Atualizado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    try {
      await api.post(`/tickets/${id}/comments`, { message });
      setMessage("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async () => {
    if (!confirm("Excluir este chamado?")) return;
    try {
      await api.delete(`/tickets/${id}`);
      toast.success("Chamado excluído");
      nav("/tickets");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      <Button variant="ghost" onClick={() => nav("/tickets")} data-testid="back-btn"><ArrowLeft className="mr-2 h-4 w-4" /> Voltar</Button>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Chamado #{ticket.id.slice(-6)}</div>
                  <CardTitle className="font-heading text-2xl mt-1">{ticket.title}</CardTitle>
                </div>
                {user.role === "admin" && (
                  <Button variant="ghost" size="icon" onClick={remove} data-testid="delete-ticket-btn">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-sm">{ticket.description}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading text-lg">Interações</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(ticket.comments || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">Nenhum comentário ainda.</p>
                )}
                {(ticket.comments || []).map((c) => (
                  <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">{c.author_name?.slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 rounded-md border border-border bg-card p-3">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span><b className="text-foreground">{c.author_name}</b> · {c.author_role}</span>
                        <span>{new Date(c.created_at).toLocaleString("pt-BR")}</span>
                      </div>
                      <p className="mt-2 whitespace-pre-wrap text-sm">{c.message}</p>
                    </div>
                  </div>
                ))}
              </div>

              <form onSubmit={addComment} className="mt-6 space-y-2">
                <Textarea rows={3} placeholder="Escreva um comentário..."
                  value={message} onChange={(e) => setMessage(e.target.value)}
                  data-testid="comment-input" />
                <div className="flex justify-end">
                  <Button type="submit" data-testid="comment-submit-btn">
                    <Send className="mr-2 h-4 w-4" /> Enviar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="font-heading text-lg">Detalhes</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</div>
                {canManage ? (
                  <Select value={ticket.status} onValueChange={(v) => update({ status: v })}>
                    <SelectTrigger className="mt-1" data-testid="status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <Badge variant="outline" className="mt-1">{ticket.status}</Badge>}
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Prioridade</div>
                {canManage ? (
                  <Select value={ticket.priority} onValueChange={(v) => update({ priority: v })}>
                    <SelectTrigger className="mt-1" data-testid="priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITY_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <Badge variant="outline" className="mt-1">{ticket.priority}</Badge>}
              </div>
              {canManage && (
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Responsável</div>
                  <Select value={ticket.assignee_id || ""} onValueChange={(v) => update({ assignee_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="assignee-select"><SelectValue placeholder="Atribuir a..." /></SelectTrigger>
                    <SelectContent>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Solicitante</div>
                <div className="mt-1">{ticket.created_by_name}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Categoria</div>
                <div className="mt-1">{ticket.category}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Aberto em</div>
                <div className="mt-1 font-mono text-xs">{new Date(ticket.created_at).toLocaleString("pt-BR")}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
